<template>
  <section class="reviews-section" aria-labelledby="reviews-title">
    <header class="reviews-header">
      <div class="reviews-heading">
        <p v-if="reviewEyebrow" class="reviews-eyebrow">{{ reviewEyebrow }}</p>
        <h2 id="reviews-title">{{ reviewTitle }}</h2>
        <p v-if="reviewSubtitle" class="reviews-subtitle">{{ reviewSubtitle }}</p>
      </div>

      <div
        v-if="ratings.length && (showAverageRating || showReviewCount)"
        class="reviews-score"
        :class="{ 'reviews-score--count-only': !showAverageRating }"
        aria-label="Resumen de calificaciones"
      >
        <strong v-if="showAverageRating">{{ averageRating }}</strong>
        <span v-if="showAverageRating" class="reviews-score__stars" aria-hidden="true">
          <b-icon
            v-for="star in 5"
            :key="star"
            :icon="star <= Math.round(Number(averageRating)) ? 'star-fill' : 'star'"
            :class="star <= Math.round(Number(averageRating)) ? 'is-filled' : 'is-empty'"
          />
        </span>
        <small v-if="showReviewCount">{{ reviewCountLabel }}</small>
        <small v-else-if="showAverageRating">Promedio sobre 5</small>
      </div>
    </header>

    <div v-if="loading" class="reviews-loading" aria-live="polite">
      <div v-for="item in 2" :key="item" class="reviews-skeleton">
        <div class="reviews-skeleton__stars"></div>
        <div class="reviews-skeleton__line reviews-skeleton__line--wide"></div>
        <div class="reviews-skeleton__line"></div>
        <div class="reviews-skeleton__line reviews-skeleton__line--short"></div>
      </div>
    </div>

    <div v-else-if="loadError" class="reviews-empty" role="status">
      <span class="reviews-empty__icon"><b-icon icon="chat-left-text" aria-hidden="true" /></span>
      <h3>No pudimos cargar las opiniones</h3>
      <p>Revisa tu conexión e inténtalo nuevamente.</p>
      <button type="button" class="reviews-retry" @click="getComments(true)">Reintentar</button>
    </div>

    <div v-else-if="filteredRatings.length" class="reviews-carousel-wrap">
      <b-carousel
        class="reviews-carousel"
        :controls="ratingsList.length > 1"
        :indicators="ratingsList.length > 1"
        :interval="ratingsList.length > 1 ? 5000 : 0"
        aria-label="Opiniones de clientes"
      >
        <b-carousel-slide v-for="(group, slideIndex) in ratingsList" :key="slideIndex">
          <div class="reviews-grid" :class="{ 'reviews-grid--single': group.length === 1 }">
            <article
              v-for="(review, reviewIndex) in group"
              :key="review._id || reviewIndex"
              class="review-card"
            >
              <div class="review-card__topline">
                <span class="review-card__stars" :aria-label="`Calificación: ${reviewRating(review)} de 5`">
                  <b-icon
                    v-for="star in 5"
                    :key="star"
                    :icon="star <= reviewRating(review) ? 'star-fill' : 'star'"
                    :class="star <= reviewRating(review) ? 'is-filled' : 'is-empty'"
                    aria-hidden="true"
                  />
                </span>
                <span v-if="review.type" class="review-card__type">
                  <b-icon icon="bag" aria-hidden="true" />
                  {{ deliveryLabel(review.type) }}
                </span>
              </div>

              <blockquote class="review-card__comment">
                {{ reviewComment(review) }}
              </blockquote>

              <div class="review-card__author">
                <span class="review-card__avatar" aria-hidden="true">{{ reviewInitials(reviewDisplayName(review)) }}</span>
                <span class="review-card__author-copy">
                  <strong v-if="reviewNameMode !== 'hidden'">{{ reviewDisplayName(review) }}</strong>
                  <small v-if="reviewVerifiedLabel">{{ reviewVerifiedLabel }}</small>
                </span>
              </div>
            </article>
          </div>
        </b-carousel-slide>
      </b-carousel>
    </div>

    <div v-else-if="ratings.length" class="reviews-empty" role="status">
      <span class="reviews-empty__icon"><b-icon icon="star" aria-hidden="true" /></span>
      <h3>Aún no hay opiniones para mostrar</h3>
      <p>No encontramos opiniones que coincidan con la configuración de esta sección.</p>
    </div>

    <div v-else class="reviews-empty">
      <span class="reviews-empty__icon"><b-icon icon="chat-left-text" aria-hidden="true" /></span>
      <h3>Aún no hay opiniones</h3>
      <p>Cuando nuestros clientes compartan su experiencia, aparecerá aquí.</p>
    </div>
  </section>
</template>

<script>
export default {
  props: {
    reviewEyebrow: {
      type: String,
      default: "EXPERIENCIAS REALES",
    },
    reviewTitle: {
      type: String,
      default: "Qué opinan nuestros clientes",
    },
    reviewSubtitle: {
      type: String,
      default: "Cada pedido cuenta. Estas son algunas de sus experiencias.",
    },
    reviewEmptyCommentText: {
      type: String,
      default: "Sin comentario escrito.",
    },
    reviewVerifiedLabel: {
      type: String,
      default: "Cliente verificado",
    },
    reviewNameMode: {
      type: String,
      default: "real",
    },
    minimumRating: {
      type: [Number, String],
      default: 0,
    },
    showReviewCount: {
      type: Boolean,
      default: true,
    },
    showAverageRating: {
      type: Boolean,
      default: true,
    },
    maxComments: {
      type: [Number, String],
      default: 6,
    },
  },
  data() {
    return {
      ratings: [],
      loading: true,
      loadError: false,
      loadedBusinessId: null,
      requestBusinessId: null,
    };
  },
  mounted() {
    this.getComments();
  },
  computed: {
    ratingsList() {
      const ratings = this.filteredRatings;
      const groups = [];
      for (let index = 0; index < ratings.length; index += 2) {
        groups.push(ratings.slice(index, index + 2));
      }
      return groups;
    },
    matchingRatings() {
      const minimumRating = Math.max(0, Math.min(5, Number(this.minimumRating) || 0));
      if (!minimumRating) return this.ratings;
      return this.ratings.filter((review) => this.reviewRating(review) >= minimumRating);
    },
    filteredRatings() {
      const maxComments = Math.max(1, Number(this.maxComments) || 6);
      return this.matchingRatings.slice(0, maxComments);
    },
    averageRating() {
      const scores = this.ratings
        .map((review) => this.reviewRating(review))
        .filter((score) => score > 0);
      if (!scores.length) return "0.0";
      return (scores.reduce((total, score) => total + score, 0) / scores.length).toFixed(1);
    },
    reviewCountLabel() {
      return `${this.ratings.length} ${this.ratings.length === 1 ? "opinión" : "opiniones"}`;
    },
    info() {
      return this.$store.state.info;
    },
  },
  watch: {
    info() {
      this.getComments();
    },
  },
  methods: {
    getComments(force = false) {
      const info = this.info || {};
      if (!info._id || !info.webName) return;
      if (!force && this.loadedBusinessId === info._id) return;
      if (this.loading && this.requestBusinessId === info._id) return;

      if (!info.pages) {
        this.$router.push(this.getLink("menu"));
        return;
      }

      this.loading = true;
      this.loadError = false;
      this.requestBusinessId = info._id;
      this.$http
        .get(`${info.webName}/rating`)
        .then((res) => {
          if (this.requestBusinessId !== info._id) return;
          this.ratings = Array.isArray(res.data.data) ? res.data.data : [];
          this.loadedBusinessId = info._id;
        })
        .catch(() => {
          if (this.requestBusinessId === info._id) this.loadError = true;
        })
        .finally(() => {
          if (this.requestBusinessId === info._id) this.loading = false;
        });
    },
    reviewRating(review = {}) {
      const rating = Number(review.rating && review.rating.note) || 0;
      return Math.max(0, Math.min(5, Math.round(rating)));
    },
    reviewComment(review = {}) {
      const comment = review.rating && review.rating.comment;
      return comment && String(comment).trim() ? String(comment).trim() : this.reviewEmptyCommentText;
    },
    reviewDisplayName(review = {}) {
      if (this.reviewNameMode === "hidden") return "Cliente";
      if (this.reviewNameMode !== "alias") return review.clientName || "Cliente";
      const aliases = ["Alex", "Camila", "Dani", "Fran", "Javi", "Luca", "Mati", "Nico", "Paz", "Sami", "Vale", "Vico"];
      const key = String(review._id || review.clientName || "cliente");
      const hash = [...key].reduce((value, character) => ((value * 31 + character.charCodeAt(0)) >>> 0), 0);
      return `Cliente ${aliases[hash % aliases.length]}`;
    },
    reviewInitials(name = "") {
      const initials = String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
      return initials || "C";
    },
    deliveryLabel(type = "") {
      const normalizedType = String(type).trim().toLowerCase();
      if (/pickup|retiro/.test(normalizedType)) return "Retiro";
      if (/delivery|domicilio|despacho/.test(normalizedType)) return "Despacho";
      if (/shipping|envio|envío/.test(normalizedType)) return "Envío";
      return String(type).trim();
    },
  },
};
</script>

<style>
.reviews-section {
  --reviews-ink: #173052;
  --reviews-muted: #6c7d93;
  --reviews-blue: #2585df;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(24px, 4vw, 52px) clamp(14px, 2.5vw, 32px);
  color: var(--reviews-ink);
}

.reviews-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.reviews-heading {
  min-width: 0;
}

.reviews-eyebrow {
  margin: 0 0 6px;
  color: #ba572f;
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.17em;
}

.reviews-heading h2 {
  margin: 0;
  color: var(--reviews-blue);
  font-size: clamp(1.65rem, 3.2vw, 2.45rem);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.reviews-subtitle {
  margin: 7px 0 0;
  color: var(--reviews-muted);
  font-size: 0.92rem;
}

.reviews-score {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: auto auto;
  align-items: center;
  column-gap: 10px;
  padding: 12px 16px;
  border: 1px solid #e8eef6;
  border-radius: 15px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(24, 53, 86, 0.07);
}

.reviews-score > strong {
  grid-row: span 2;
  color: var(--reviews-ink);
  font-size: 1.9rem;
  font-weight: 850;
  letter-spacing: -0.06em;
  line-height: 1;
}

.reviews-score__stars {
  display: flex;
  gap: 2px;
  color: #ffb300;
  font-size: 0.76rem;
}

.reviews-score__stars .is-empty {
  color: #d7e0ea;
}

.reviews-score > small {
  color: var(--reviews-muted);
  font-size: 0.68rem;
}

.reviews-score--count-only {
  display: flex;
  align-items: center;
  gap: 9px;
}

.reviews-score--count-only > small {
  color: var(--reviews-ink);
  font-size: 0.84rem;
  font-weight: 750;
}

.reviews-carousel-wrap {
  overflow: hidden;
  padding: 16px 16px 12px;
  border: 1px solid #e8edf4;
  border-radius: 22px;
  background:
    radial-gradient(circle at 4% 0%, rgba(218, 235, 255, 0.8), transparent 33%),
    linear-gradient(135deg, #f8fbff, #f2f6fb);
}

.reviews-carousel .carousel-caption {
  position: relative;
  right: auto;
  bottom: auto;
  left: auto;
  padding: 4px 0 16px;
  color: inherit;
  text-align: left;
}

.reviews-carousel .carousel-indicators {
  bottom: -2px;
  margin-bottom: 0;
}

.reviews-carousel .carousel-indicators li {
  width: 7px;
  height: 7px;
  margin: 0 4px;
  border: 0;
  border-radius: 999px;
  background-color: #89a8ca;
  opacity: 0.5;
}

.reviews-carousel .carousel-indicators li.active {
  width: 21px;
  background-color: var(--reviews-blue);
  opacity: 1;
}

.reviews-carousel .carousel-control-prev,
.reviews-carousel .carousel-control-next {
  top: 50%;
  bottom: auto;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(220, 231, 245, 0.85);
  border-radius: 50%;
  background-color: #fff;
  opacity: 1;
  transform: translateY(-50%);
  box-shadow: 0 4px 14px rgba(23, 48, 82, 0.12);
}

.reviews-carousel .carousel-control-prev { left: 2px; }
.reviews-carousel .carousel-control-next { right: 2px; }

.reviews-carousel .carousel-control-prev-icon,
.reviews-carousel .carousel-control-next-icon {
  filter: invert(34%) sepia(58%) saturate(1258%) hue-rotate(182deg) brightness(93%) contrast(91%);
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  width: 100%;
  padding: 0 38px;
}

.reviews-grid--single .review-card {
  grid-column: 1 / -1;
  width: min(100%, 760px);
  margin: 0 auto;
}

.review-card {
  display: flex;
  min-width: 0;
  min-height: 194px;
  flex-direction: column;
  padding: 18px 20px 16px;
  border: 1px solid rgba(224, 232, 242, 0.95);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 7px 20px rgba(32, 56, 83, 0.065);
}

.review-card__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.review-card__stars {
  display: inline-flex;
  gap: 3px;
  color: #f4ad12;
  font-size: 0.94rem;
}

.review-card__stars .is-empty {
  color: #d7e0ea;
}

.review-card__type {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #edf5ff;
  color: #42688f;
  font-size: 0.64rem;
  font-weight: 750;
  white-space: nowrap;
}

.review-card__comment {
  flex: 1;
  margin: 14px 0 17px;
  color: #33445c;
  font-size: 0.94rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.review-card__author {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #edf1f6;
  background: transparent !important;
  color: var(--reviews-ink);
}

.review-card__avatar {
  display: grid;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #dbeeff, #c7def8);
  color: #2868a6;
  font-size: 0.72rem;
  font-weight: 850;
}

.review-card__author-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.review-card__author-copy strong {
  overflow: hidden;
  color: var(--reviews-ink);
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-card__author-copy small {
  margin-top: 2px;
  color: #8190a4;
  font-size: 0.65rem;
}

.reviews-loading {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 16px;
  border: 1px solid #e8edf4;
  border-radius: 22px;
  background: #f5f8fc;
}

.reviews-skeleton {
  min-height: 194px;
  padding: 20px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 7px 20px rgba(32, 56, 83, 0.045);
}

.reviews-skeleton__stars,
.reviews-skeleton__line {
  height: 11px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7, #e5ebf2, #eef2f7);
  background-size: 200% 100%;
  animation: reviews-shimmer 1.5s ease-in-out infinite;
}

.reviews-skeleton__stars {
  width: 78px;
  margin-bottom: 28px;
}

.reviews-skeleton__line {
  width: 78%;
  margin-bottom: 12px;
}

.reviews-skeleton__line--wide { width: 96%; }
.reviews-skeleton__line--short { width: 52%; }

.reviews-empty {
  padding: 38px 18px;
  border: 1px dashed #cfdae7;
  border-radius: 18px;
  background: #f7faff;
  color: var(--reviews-muted);
  text-align: center;
}

.reviews-empty__icon {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  margin: 0 auto 10px;
  border-radius: 15px;
  background: #e7f1fc;
  color: var(--reviews-blue);
  font-size: 1.2rem;
}

.reviews-empty h3 {
  margin: 0;
  color: var(--reviews-ink);
  font-size: 1rem;
  font-weight: 800;
}

.reviews-empty p {
  margin: 6px 0 0;
  font-size: 0.83rem;
}

.reviews-retry {
  margin-top: 13px;
  padding: 8px 15px;
  border: 0;
  border-radius: 999px;
  background: var(--reviews-blue);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 750;
}

@keyframes reviews-shimmer {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}

@media (max-width: 767.98px) {
  .reviews-header {
    align-items: flex-start;
  }

  .reviews-score {
    grid-template-columns: auto;
    justify-items: center;
    row-gap: 4px;
    padding: 10px 12px;
  }

  .reviews-score > strong {
    grid-row: auto;
    font-size: 1.5rem;
  }

  .reviews-carousel-wrap {
    padding: 12px 8px 10px;
  }

  .reviews-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    padding: 0 32px;
  }

  .review-card {
    min-height: 180px;
    padding: 15px;
  }

  .reviews-carousel .carousel-caption {
    padding-bottom: 17px;
  }

  .reviews-loading {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
  }

  .reviews-skeleton:last-child {
    display: none;
  }
}

@media (max-width: 479.98px) {
  .reviews-section {
    padding: 24px 12px 32px;
  }

  .reviews-header {
    flex-direction: column;
    gap: 14px;
  }

  .reviews-score {
    grid-template-columns: auto auto;
    justify-items: start;
    width: 100%;
  }

  .reviews-score > strong {
    grid-row: span 2;
  }

  .reviews-score__stars,
  .reviews-score > small {
    justify-self: start;
  }

  .review-card__comment {
    font-size: 0.88rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reviews-skeleton__stars,
  .reviews-skeleton__line {
    animation: none;
  }
}
</style>
